import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import style from './style.module.scss';
import { IProps, IHandlers } from './types';
import { FormControl, InputLabel, MenuItem, Select, MenuProps, TextField } from '@material-ui/core';
import moment from 'moment';
import { renderIf } from 'external-services/react-utils-service/index';
import { getMaterialStyles } from './helpers';
import LocationService from 'external-services/location-service';
import classNames from "classnames";

const NS = 'TimeSelector';

const isMobileOrTablet: boolean = LocationService.isIOS() || LocationService.isAndroid();

export default function TimeSelector({
  preselectedTimeIndex,
  preselectedTime,
  times,
  isCustomTime,
  useReversedColors,
  handleSelectedChanged,
  isLockedBooking,
}: IProps & IHandlers) {
  const [selectedIndex, setSelectedIndex] = useState<number>(preselectedTimeIndex || 0);
  const [selectedTime, setSelectedTime] = useState<string>('');

  // compares in 24 hour time
  if (isCustomTime && !selectedTime) {
    let preselectTime24: string;
    if (preselectedTime) {
      preselectTime24 = moment(preselectedTime).format('HH:mm'); // 24 hour time
    } else if (times && times[selectedIndex]) {
      preselectTime24 = moment(times[selectedIndex].time).format('HH:mm'); // 24 hour time
    }

    if (preselectTime24 !== selectedTime) {
      setSelectedTime(preselectTime24);
    }
  }

  const handleSelectChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const index: number = event.target.value as number;
    setSelectedIndex(index);
    handleSelectedChanged(index, null);
  };

  const handleCustomTimeChange = ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
    setSelectedIndex(-1);
    setSelectedTime(currentTarget.value);
    handleSelectedChanged(-1, currentTarget.value);
  };

  if (preselectedTimeIndex !== -1 && preselectedTimeIndex !== selectedIndex) {
    setSelectedIndex(preselectedTimeIndex);
  }

  const MenuProps: Partial<MenuProps> = {
    PaperProps: {
      style: {
        maxHeight: 550,
      },
    },
    transitionDuration: isMobileOrTablet ? 0 : 'auto',
  };

  const useStyles = useMemo(() => getMaterialStyles(useReversedColors, true), [useReversedColors]);
  const classes = useStyles();

  return (
    <div className={style.root}>
      {renderIf(
        isCustomTime,
        () => (
          <form noValidate>
            <TextField
              className={classNames({
                // [classes.textField]: true,
                // [style.textField]: true,
                [style.textFieldReversedColors]: useReversedColors
              })}
              label="Time"
              type="time"
              value={selectedTime}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                step: 60, // 1 min
              }}
              onChange={handleCustomTimeChange}
            />
          </form>
        ),
        () => (
          <FormControl className={classes.selectFC}>
            <InputLabel
              data-testid="select-label"
              id="time-selector-label"
              className={classes.inputLabel}
            >
              Time
            </InputLabel>

            <Select
              disabled={isLockedBooking}
              data-testid="select-root"
              className={classes.select}
              labelId="time-selector-label"
              id="time-selector"
              value={selectedIndex}
              MenuProps={MenuProps}
              onChange={handleSelectChange}
              placeholder="Select time"
              renderValue={(selected: unknown) => {
                const selectedIndex: number = selected as number;

                if (selectedIndex === -1) {
                  return 'Select time';
                }

                return times ? moment(times[selectedIndex].time).format('h:mma') : 'No times';
              }}
            >
              {times &&
                times.map(({ time }, i) => (
                  <MenuItem key={'time-' + i} value={i} className={classes.menuItem}>
                    {moment(time).format('h:mma')}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )
      )}
    </div>
  );
}
